import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircleAlert, MailWarning } from "lucide-react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/Spinner";
import StatusScreen, { FullPageSpinner } from "@/components/StatusScreen";
import AuthLayout from "@/components/layout/AuthLayout";
import NewPasswordFields, {
  EMPTY_NEW_PASSWORD,
  isValidNewPassword,
} from "../../fragments/NewPasswordFields";
import { acceptInvitation, lookupInvitation } from "../../utils";
import { tokenFromHash } from "../../utils/links";
import { useSession } from "../../session/SessionContext";

interface Invitation {
  email: string;
  organization: { id: string; name: string };
}

const AcceptInvitation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn } = useSession();
  const token = tokenFromHash(location.hash);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [value, setValue] = useState(EMPTY_NEW_PASSWORD);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    lookupInvitation(token)
      .then(setInvitation)
      .catch((error: Error) => setLoadError(error.message));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!isValidNewPassword(value)) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { token: sessionToken } = await acceptInvitation(token, value.password);
      await signIn(sessionToken);
      navigate("/", { replace: true });
    } catch (error) {
      setSubmitError((error as Error).message);
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <StatusScreen
        icon={MailWarning}
        tone="warning"
        title="Deze uitnodiging kan niet worden gebruikt"
        description={`${loadError} Vraag degene die je uitnodigde om een nieuwe link.`}
        actions={
          <Button variant="outline" onClick={() => navigate("/login")}>
            Naar inloggen
          </Button>
        }
      />
    );
  }
  if (!invitation) {
    return <FullPageSpinner />;
  }

  return (
    <AuthLayout
      title={`Word lid van ${invitation.organization.name}`}
      description={
        <>
          Je maakt een account aan als{" "}
          <strong className="font-medium text-foreground">{invitation.email}</strong>. Kies een
          wachtwoord om af te ronden.
        </>
      }
    >
      <form className="grid gap-4" noValidate onSubmit={handleSubmit}>
        <NewPasswordFields value={value} onChange={setValue} showErrors={submitted} />
        {submitError && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>{submitError}</AlertTitle>
          </Alert>
        )}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Spinner />}
          Account aanmaken
        </Button>
      </form>
    </AuthLayout>
  );
};

export default AcceptInvitation;
